import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '@confirm-dialog';
import { InputFileConfig } from '@core/models/built/procedure-step-config.model';
import { InputJson } from '@core/models/built/session-step-value-json.model';
import { SessionStepValueType } from '@core/models/built/session-step-value-type.enum';
import { SessionStep, SessionStepValue } from '@core/models/session.model';
import { TranslocoService } from '@ngneat/transloco';
import { filterTruthy } from '@utils';
import { DateTime } from 'luxon';
import { BehaviorSubject, from, Subject, Subscription } from 'rxjs';
import { first, map, withLatestFrom } from 'rxjs/operators';

import it from './i18n/input-image-session.it.json';

@Component({
  templateUrl: './input-image-session.component.html'
})
export class InputImageSessionComponent implements OnInit, OnDestroy, OnChanges {

  @Input() step!: SessionStep;

  @Output() commitAndNext = new EventEmitter<SessionStep | [SessionStep, File[]]>();

  inputForm: FormGroup = this.fb.group({
    value: [undefined, [Validators.required]]
  });

  private step$$ = new BehaviorSubject<SessionStep | undefined>(undefined);
  step$ = this.step$$.pipe(filterTruthy());

  private takeShotEvt$$ = new Subject<{ video: HTMLVideoElement; canvas: HTMLCanvasElement }>();

  readonly config = {
    video: {
      facingMode: 'environment'
    }
  };

  videoStream$ = from(navigator.mediaDevices.getUserMedia(this.config));
  fileAcquisitionMax$ = this.step$.pipe(map(step => (step.procedureStep?.config as InputFileConfig).maxFiles || 1));

  private acquired$$ = new BehaviorSubject<string[]>([]);
  acquired$ = this.acquired$$.asObservable();

  showTakeShot$ = this.acquired$.pipe(
    withLatestFrom(this.fileAcquisitionMax$),
    map(([acquired, fileAcquisitionMax]) => acquired.length < fileAcquisitionMax)
  );

  private preview$$ = new BehaviorSubject<{ height: number; width: number; src: string } | undefined>(undefined);
  preview$ = this.preview$$.asObservable();

  private subs = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private translocoSvc: TranslocoService
  ) {
    this.translocoSvc.setTranslation(it, 'it');
  }

  ngOnInit() {
    if (!!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
    } else {
      alert('getUserMedia() is not supported by your browser');
    }
    this.subs.add(
      this.takeShotEvt$$.subscribe(({ video, canvas }) => {
        canvas.height = video.videoHeight;
        canvas.width = video.videoWidth;
        canvas.getContext('2d')?.drawImage(video, 0, 0);
        this.preview$$.next({
          height: video.videoHeight,
          width: video.videoWidth,
          src: canvas.toDataURL()
        });
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.step) {
      this.step$$.next(changes.step.currentValue);
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  takeShot(video: HTMLVideoElement, canvas: HTMLCanvasElement) {
    this.takeShotEvt$$.next({ video, canvas });
  }

  confirmPreview(dataUrl: string) {
    this.acquired$$.next([...this.acquired$$.getValue(), dataUrl]);
    this.discardPreview();
  }

  discardPreview() {
    this.preview$$.next(undefined);
  }

  removeFromAcquired(dataUrl: string) {
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translocoSvc.translate('input-image-session.messages.confirmRemoveTitle'),
        message: this.translocoSvc.translate('input-image-session.messages.confirmRemoveMessage')
      }
    }).afterClosed().subscribe(response => {
      if (response) {
        this.acquired$$.next(this.acquired$$.getValue().filter(url => url !== dataUrl));
      }
    });
  }

  submit(step: SessionStep) {
    this.acquired$.pipe(
      withLatestFrom(this.fileAcquisitionMax$),
      first()
    ).subscribe(([acquired, fileAcquisitionMax]) => {
      if (acquired.length > 0 && acquired.length <= fileAcquisitionMax) {
        const now = DateTime.local();
        const value: SessionStepValue = {
          workflowId: step.workflowId,
          workflowStepId: step.workflowStepId,
          sessionId: step.sessionId,
          timestamp: now.toJSDate(),
          type: SessionStepValueType.file,
          value: { input: undefined } as InputJson
        };
        this.commitAndNext.emit([{ ...this.step, completedOn: now.toJSDate(), values: [value] }, acquired as unknown as File[]]);
      }
    });
  }

}
